/**
 * Shared helpers for the per-client Effect RPC modules.
 *
 * Every `*RpcClient` lives in its own file under `rpc-clients/` and imports
 * the protocol/token plumbing from here. This keeps the addition of a new
 * RPC client a NEW-file operation (never an append to a shared registry),
 * which eliminates the silent semantic-merge collisions that produced
 * duplicate class declarations when two PRs appended to the old monolithic
 * `rpc-client.ts`.
 *
 * @since 1.0.0
 */
import { HttpClient, HttpClientRequest } from "@effect/platform";
import { RpcClient, RpcMiddleware } from "@effect/rpc";
import { InstallerTokenMiddleware, MgmtAgentMiddleware } from "@fm/api/rpc";
import { Context, Data, Duration, Effect, Layer, Option, Schema } from "effect";
import { hasClerkSessionCookie, jwtExpiryMs, readClerkSessionCookieToken } from "../clerk-session-cookie.ts";

/**
 * Type for streaming message request payload
 * @since 1.0.0
 */
export const StreamMessageRequestSchema = Schema.Struct({
  conversationId: Schema.String,
  content: Schema.String,
  systemPrompt: Schema.optional(Schema.String),
});

export type StreamMessageRequest = typeof StreamMessageRequestSchema.Type;

/**
 * Error thrown when Clerk token retrieval fails
 *
 * @since 1.0.0
 */
export class ClerkTokenError extends Data.TaggedError("ClerkTokenError")<{
  readonly cause: unknown;
}> {}

/**
 * Service interface for getting Clerk authentication tokens
 *
 * @since 1.0.0
 */
export interface ClerkTokenGetterService {
  readonly getToken: () => Effect.Effect<string | null, ClerkTokenError>;
}

/**
 * Context tag for accessing the Clerk token getter function
 *
 * This allows the RPC client to retrieve fresh tokens from Clerk.
 * The token getter should be provided by the React component layer.
 *
 * @since 1.0.0
 */
export class ClerkTokenGetter extends Context.Tag("ClerkTokenGetter")<
  ClerkTokenGetter,
  ClerkTokenGetterService
>() {
  static readonly Default = Layer.succeed(this, {
    getToken: () => Effect.succeed(null),
  });
}

/**
 * Creates an RPC client protocol layer with Clerk authentication
 *
 * Creates an HTTP protocol layer that:
 * - Sends requests to the configured API URL (from VITE_API_INTERNAL_URL)
 * - Adds Authorization header with Clerk session token
 * - Sets Content-Type to application/json
 *
 * The API URL is determined by the VITE_API_INTERNAL_URL environment variable:
 * - Dev/Default: "/api/rpc" (proxied by server.ts to API_URL)
 * - Production: Public API URL (e.g., "https://api.flirtmachine.app/rpc")
 *
 * @param schema - RPC schema to create client for
 * @returns Layer providing the RPC Protocol
 *
 * @example
 * ```typescript
 * import { AuthRpcs } from "@fm/api/rpc";
 *
 * // Provide the token getter from React component
 * const { getToken } = useAuth();
 *
 * const tokenGetterLayer = Layer.succeed(ClerkTokenGetter, {
 *   getToken: () => getToken()
 * });
 *
 * const program = Effect.gen(function*() {
 *   const clientLayer = makeRpcClientLayer(AuthRpcs);
 *   const client = yield* RpcClient.make(AuthRpcs);
 *   const me = yield* client.me({});
 *   return me;
 * }).pipe(
 *   Effect.provide(clientLayer),
 *   Effect.provide(tokenGetterLayer)
 * );
 * ```
 *
 * @since 1.0.0
 */
/**
 * Resolve the RPC API URL from the (optional) `VITE_API_INTERNAL_URL` build
 * env value, falling back to the dev/default `/api/rpc` proxy path.
 *
 * Extracted as a pure function so both branches of the fallback are unit
 * testable — Vite inlines `import.meta.env.*` at build time, which makes the
 * `??` branch on the raw property access uncoverable in place.
 *
 * @since 1.0.0
 */
export const resolveApiUrl = (envUrl: string | undefined): string => envUrl ?? "/api/rpc";

export const makeRpcClientLayer = <S>(_schema: S, extraHeaders?: Readonly<Record<string, string>>) =>
  Layer.unwrapScoped(
    ClerkTokenGetter.pipe(
      Effect.map((tokenGetter) => {
        const apiUrl = resolveApiUrl(import.meta.env.VITE_API_INTERNAL_URL);
        return RpcClient.layerProtocolHttp({
          url: apiUrl,
          transformClient: (client) =>
            client.pipe(
              HttpClient.mapRequestEffect((req) =>
                tokenGetter.getToken().pipe(
                  Effect.tapError((err) => Effect.logWarning(`[RPC Client] Token fetch failed: ${String(err)}`)),
                  Effect.catchAll(() => Effect.succeed(null)),
                  Effect.map((token) => {
                    const headers: Record<string, string> = {
                      "Content-Type": "application/json",
                      ...extraHeaders,
                    };
                    if (token) {
                      headers["Authorization"] = `Bearer ${token}`;
                    }
                    return HttpClientRequest.setHeaders(req, headers);
                  }),
                )
              ),
            ),
        });
      }),
    ),
  );

/**
 * Minimal shape of the Clerk session object the token getter depends on: just
 * enough to mint a (freshly rotated) session JWT. Declared as a shared
 * interface so the `globalThis.__clerkSession` bridge and the hydration-wait
 * helper agree on one type.
 *
 * @since 1.0.0
 */
export interface ClerkSessionLike {
  readonly getToken: () => Promise<string | null>;
}

declare global {
  // Set by ClerkAuthSync component: the live SDK session when one exists, or
  // `null` before clerk-js hydrates (it writes `clerk.session ?? null`), and
  // `undefined` before the component has ever run. Consumers must treat both
  // `null` and `undefined` as "no live session yet" — the token getters here
  // (`if (session)`) and `waitForClerkSession` (`?? null`) already do, and
  // `resolveViewerToken` in OrcaTerminal.tsx now guards both (OL-19).
  var __clerkSession: ClerkSessionLike | null | undefined;
}

// `jwtExpiryMs`, `readClerkSessionCookieToken` and `hasClerkSessionCookie`
// moved to `~/lib/clerk-session-cookie.ts` (they need only `effect` core) so
// that `atoms/clerk-auth.ts` can read the session token without dragging this
// module's `@effect/rpc` + `@effect/platform` plumbing into the always-loaded
// `main` chunk (CLA-645). Re-exported here so existing importers are unaffected.
export { hasClerkSessionCookie, jwtExpiryMs, readClerkSessionCookieToken };

/**
 * Tuning for the {@link makeTokenGetterLayer} hydration wait. Both fields
 * default to the production constants below; tests inject small values so the
 * timeout branch resolves fast.
 *
 * @since 1.0.0
 */
export interface TokenGetterConfig {
  readonly pollMs?: number;
  readonly timeoutMs?: number;
}

/**
 * Interval between `globalThis.__clerkSession` presence checks while waiting
 * for clerk-js to hydrate.
 *
 * @since 1.0.0
 */
export const DEFAULT_HYDRATION_POLL_MS = 100;

/**
 * Upper bound on how long the token getter waits for clerk-js to hydrate
 * before giving up and falling through to the tokenless path. Sized to comfortably
 * cover clerk-js boot (~1–2 s) without hanging a genuinely signed-out browser.
 *
 * @since 1.0.0
 */
export const DEFAULT_HYDRATION_TIMEOUT_MS = 8000;

/**
 * Fetch a token from the Clerk SDK session, wrapping any rejection in a typed
 * {@link ClerkTokenError}. Shared by the immediate-session path and the
 * post-hydration path of {@link makeTokenGetterLayer}.
 *
 * @since 1.0.0
 */
export const sdkGetToken = (session: ClerkSessionLike): Effect.Effect<string | null, ClerkTokenError> =>
  Effect.tryPromise({
    try: (): Promise<string | null> => session.getToken(),
    catch: (error) => new ClerkTokenError({ cause: error }),
  });

/**
 * Bounded poll for `globalThis.__clerkSession` to be populated by
 * `ClerkAuthSync` once clerk-js finishes booting, resolving to the session (or
 * `null` if it never appears within `timeoutMs`).
 *
 * The OAuth callback landing page (and any RPC firing on first paint) can
 * dispatch before clerk-js has hydrated. When the `__session` cookie has ALSO
 * aged out during a slow provider consent round-trip — write-scope consent
 * screens routinely exceed the ~60 s Clerk JWT lifetime — there is no token to
 * send and the request is rejected `Unauthorized` in ~1 ms (CLA-792). Rather
 * than fail tokenless, wait up to `timeoutMs` (polling every `pollMs`) for the
 * SDK session to appear so the caller can mint a fresh token from it. On
 * timeout the caller falls through to the existing null behaviour, so a
 * genuinely signed-out browser still fails fast instead of hanging.
 *
 * @since 1.0.0
 */
export const waitForClerkSession = (
  pollMs: number,
  timeoutMs: number,
): Effect.Effect<ClerkSessionLike | null> => {
  const poll = (): Effect.Effect<ClerkSessionLike | null> =>
    Effect.sync((): ClerkSessionLike | null => globalThis.__clerkSession ?? null).pipe(
      Effect.flatMap((session: ClerkSessionLike | null): Effect.Effect<ClerkSessionLike | null> =>
        session !== null
          ? Effect.succeed(session)
          : Effect.sleep(Duration.millis(pollMs)).pipe(Effect.flatMap(() => poll()))
      ),
    );
  return poll().pipe(
    Effect.timeoutOption(Duration.millis(timeoutMs)),
    Effect.map((maybe): ClerkSessionLike | null => Option.getOrElse(maybe, (): ClerkSessionLike | null => null)),
  );
};

/**
 * Token getter layer used by every Clerk-authed RPC client below.
 *
 * Primary path: ask the Clerk SDK for a token via `session.getToken()`.
 * The SDK transparently refreshes when the JWT is within its TTL
 * window (Clerk rotates ~every 60 s), so this is the only source that
 * is guaranteed-fresh on every request. Using the raw cookie value
 * directly causes per-minute `Unauthorized` bursts in the window
 * between expiry and the SDK's background rotation.
 *
 * Cookie path: read the `__session` cookie synchronously. The cookie
 * IS the JWT (Clerk publishes it without `HttpOnly` for this exact
 * reason) and is set before the SDK has finished its async boot, so
 * this rescues the first-paint window where `globalThis.__clerkSession`
 * hasn't been populated yet by `ClerkAuthSync`.
 *
 * Hydration-wait path (CLA-792): when there is neither a live SDK session nor
 * an unexpired cookie BUT a `__session` cookie is present-and-expired, the
 * caller was signed in and its JWT merely aged out (e.g. during a >60 s OAuth
 * consent round-trip). Firing tokenless here yields an instant `Unauthorized`
 * and a dead connect flow. Instead, wait — bounded by
 * {@link DEFAULT_HYDRATION_TIMEOUT_MS} — for clerk-js to hydrate
 * `globalThis.__clerkSession`, then mint a fresh token from it. A WHOLLY ABSENT
 * cookie (signed-out / public installer caller) skips the wait and keeps the
 * fast tokenless path, so the public flows are never delayed. On timeout the
 * getter falls through to `null` and the request proceeds tokenless as before.
 *
 * `config` overrides the poll interval / timeout; production callers pass
 * nothing and get the module defaults. Exported so unit tests can exercise
 * every branch directly without having to construct a full `AtomRpc.Tag`.
 *
 * @since 1.0.0
 */
export const makeTokenGetterLayer = (config?: TokenGetterConfig) =>
  Layer.succeed(ClerkTokenGetter, {
    getToken: (): Effect.Effect<string | null, ClerkTokenError> => {
      const session = globalThis.__clerkSession;
      if (session) {
        return sdkGetToken(session);
      }
      const cookieToken = readClerkSessionCookieToken();
      if (cookieToken !== null) {
        return Effect.succeed(cookieToken);
      }
      // No live session and no usable cookie. Only wait for hydration when an
      // expired session cookie is actually present (the user WAS signed in);
      // a missing cookie means signed-out / public — stay fast and tokenless.
      if (!hasClerkSessionCookie()) {
        return Effect.succeed(null);
      }
      return waitForClerkSession(
        config?.pollMs ?? DEFAULT_HYDRATION_POLL_MS,
        config?.timeoutMs ?? DEFAULT_HYDRATION_TIMEOUT_MS,
      ).pipe(
        Effect.flatMap((hydrated): Effect.Effect<string | null, ClerkTokenError> =>
          hydrated !== null ? sdkGetToken(hydrated) : Effect.succeed(null)
        ),
      );
    },
  });

/**
 * Stub client for `InstallerTokenMiddleware`. The web only calls the
 * Clerk-authed stream endpoint; the installer-only emit RPCs guarded
 * by this middleware are never invoked from the browser, but the RPC
 * group still requires a client middleware layer to satisfy
 * `requiredForClient: true`. Returning the request untouched is safe
 * because no installer-token RPCs are ever dispatched here.
 *
 * @since 1.0.0
 */
export const InstallerTokenMiddlewareClientLive = RpcMiddleware.layerClient(
  InstallerTokenMiddleware,
  ({ request }) => Effect.succeed(request),
);

/**
 * Client-side pass-through for `MgmtAgentMiddleware`. The middleware is
 * server-gated by a gateway token but is declared as `requiredForClient:
 * true`, so the protocol layer must provide a client implementation. The
 * actual server enforcement is unaffected — operator UI rollback writes
 * will only succeed if the request carries valid gateway-token credentials
 * (future work; v1 logs intent only).
 *
 * Exported for unit-testing; consumers should compose via `ProfileRpcClient`.
 *
 * @since 1.0.0
 */
export const MgmtAgentMiddlewareClientLive = RpcMiddleware.layerClient(
  MgmtAgentMiddleware,
  ({ request }) => Effect.succeed(request),
);
